import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  Requirement,
  RequirementApplyMode,
  RequirementType,
} from "../api/useRequirements";

/* ------------------------------------------------------------------ */
/* STATE                                                               */
/* ------------------------------------------------------------------ */

/**
 * WHICH pipeline the form writes to. "stage" = the selected stage's statuses
 * (the original, and the default); "track" = a track's statuses (Tracks
 * Management → Requirements, 2026-11-02). In track scope `selectedStatusId`
 * / `selectedStatusIds` hold TRACK STATUS ids, sub-statuses are never
 * offered, and the transition-time gates (Notes / FollowUps) and info-only
 * rows do not exist.
 */
export type RequirementScope = "stage" | "track";

export interface RequirementFunctionalState {
  isAddRequirement: boolean;
  scope: RequirementScope;
  /** Track scope only — the track (as its `TrackConfig.id`) the form belongs to. */
  trackId: string | null;
  /** Hierarchy mode's single status; also the FIRST of `selectedStatusIds`. */
  selectedStatusId: string;
  /**
   * SPECIFIC mode only — the statuses this requirement is added to. A
   * specific gate fires on exactly one status, so "the same rule on five
   * statuses" is five rows; the modal writes them in one save instead of
   * making the user reopen it five times (user request 2026-09-01).
   * Hierarchy stays single-select: it already cascades to later statuses,
   * so picking several would just be a slower way to pick the earliest.
   */
  selectedStatusIds: string[];
  /**
   * SUB-STATUS NARROWING (2026-09-29) — OPTIONAL. Empty = the requirement
   * covers the whole status, which is the original behaviour and stays the
   * default. Picking sub-statuses writes ONE ROW PER SUB-STATUS (the same
   * shape `selectedStatusIds` already uses for statuses), so a gate can sit on
   * "Fee pending" and "Docs pending" but not on "Fee paid".
   *
   * Only ids belonging to the currently selected status(es) are ever saved —
   * the modal intersects before writing, so a stale pick can't leak.
   */
  selectedSubStatusIds: string[];
  /**
   * EDIT MODE. Null = the form is adding new rows (the original behaviour).
   * Set = the form is bound to that one existing requirement and Save PATCHes
   * it in place, so an admin can fix a gate's type, reference, enforcement,
   * message, roles or bucket instead of deleting and re-adding it.
   */
  editingRequirementId: number | null;
  type: RequirementType | "";
  referenceKey: string;
  referenceId: number | null;
  isBlocking: boolean;
  /** Info-only (Documents type only): shows on the attachments checklist — never blocks, never warns. */
  isInfo: boolean;
  errorMessage: string;
  /** hierarchy = from this status onward (default); specific = only when changing to this status. */
  applyMode: RequirementApplyMode;
  /** Specific mode only — also create a "Notes" gate for the same status on save. */
  alsoRequireNote: boolean;
  /** Roles this requirement does NOT apply to (empty = every role). */
  excludedRoleIds: number[];
  deleteRequirementId: number | null;
}

const DEFAULT_ERROR_MESSAGE = "One of the Required Field Is Missing";
export const NOTE_REQUIREMENT_ERROR_MESSAGE =
  "A note is required when moving to this status";
export const FOLLOWUP_REQUIREMENT_ERROR_MESSAGE =
  "Schedule a follow-up before moving to this status";

const initialState: RequirementFunctionalState = {
  isAddRequirement: false,
  scope: "stage",
  trackId: null,
  selectedStatusId: "",
  selectedStatusIds: [],
  selectedSubStatusIds: [],
  editingRequirementId: null,
  type: "",
  referenceKey: "",
  referenceId: null,
  isBlocking: true,
  isInfo: false,
  errorMessage: DEFAULT_ERROR_MESSAGE,
  applyMode: "hierarchy",
  alsoRequireNote: false,
  excludedRoleIds: [],
  deleteRequirementId: null,
};

/* ------------------------------------------------------------------ */
/* SLICE                                                               */
/* ------------------------------------------------------------------ */

const requirementFunctionalSlice = createSlice({
  name: "requirementFunctional",
  initialState,
  reducers: {
    setIsAddRequirement(state, action: PayloadAction<boolean>) {
      state.isAddRequirement = action.payload;
    },
    /**
     * Open the form for a TRACK's statuses. A fresh form every time: the
     * stage form's picks mean nothing on a track (and vice versa), so nothing
     * carries over between the two scopes.
     */
    openTrackRequirementForm(_state, action: PayloadAction<string>) {
      return {
        ...initialState,
        isAddRequirement: true,
        scope: "track",
        trackId: action.payload,
      };
    },
    setRequirementSelectedStatusId(state, action: PayloadAction<string>) {
      const changed = state.selectedStatusId !== action.payload;
      state.selectedStatusId = action.payload;
      state.selectedStatusIds = action.payload ? [action.payload] : [];
      // Sub-statuses belong to a status — moving the status makes any pick
      // under the old one meaningless, so it resets to "all sub-statuses".
      if (changed) state.selectedSubStatusIds = [];
    },
    /** Specific mode's multi-pick. `selectedStatusId` tracks the first so
     *  every existing single-status consumer keeps working unchanged. */
    setRequirementSelectedStatusIds(state, action: PayloadAction<string[]>) {
      const dropped = state.selectedStatusIds.filter(
        (id) => !action.payload.includes(id)
      );
      state.selectedStatusIds = action.payload;
      state.selectedStatusId = action.payload[0] ?? "";
      // Only un-picking a status can orphan a sub-status pick; adding one
      // never does, so a multi-status build-up keeps what's already chosen.
      if (dropped.length) state.selectedSubStatusIds = [];
    },
    /** The optional sub-status narrowing. Empty = the whole status. */
    setRequirementSelectedSubStatusIds(state, action: PayloadAction<string[]>) {
      state.selectedSubStatusIds = action.payload;
    },
    setRequirementType(state, action: PayloadAction<RequirementType | "">) {
      const leavingNotes = state.type === "Notes" && action.payload !== "Notes";
      const leavingFollowUps =
        state.type === "FollowUps" && action.payload !== "FollowUps";
      state.type = action.payload;
      state.referenceId = null;
      // Info-only mode is a Documents-only concept — leaving Documents falls
      // back to a normal blocking gate.
      if (action.payload !== "Documents" && state.isInfo) {
        state.isInfo = false;
        state.isBlocking = true;
      }
      if (action.payload === "Notes") {
        // A Notes gate has a fixed reference and a tailored default message.
        state.referenceKey = "Note";
        if (state.errorMessage === DEFAULT_ERROR_MESSAGE) {
          state.errorMessage = NOTE_REQUIREMENT_ERROR_MESSAGE;
        }
        state.alsoRequireNote = false;
      } else if (action.payload === "FollowUps") {
        // Same shape as Notes: fixed reference, tailored default message.
        state.referenceKey = "FollowUp";
        if (state.errorMessage === DEFAULT_ERROR_MESSAGE) {
          state.errorMessage = FOLLOWUP_REQUIREMENT_ERROR_MESSAGE;
        }
        state.alsoRequireNote = false;
      } else {
        state.referenceKey = "";
        if (leavingNotes && state.errorMessage === NOTE_REQUIREMENT_ERROR_MESSAGE) {
          state.errorMessage = DEFAULT_ERROR_MESSAGE;
        }
        if (
          leavingFollowUps &&
          state.errorMessage === FOLLOWUP_REQUIREMENT_ERROR_MESSAGE
        ) {
          state.errorMessage = DEFAULT_ERROR_MESSAGE;
        }
      }
    },
    setRequirementApplyMode(state, action: PayloadAction<RequirementApplyMode>) {
      state.applyMode = action.payload;
      if (action.payload === "hierarchy") {
        state.alsoRequireNote = false;
        // Hierarchy is single-status — collapse any multi-pick to the first.
        state.selectedStatusIds = state.selectedStatusId
          ? [state.selectedStatusId]
          : [];
        // The transition-time gates only exist in specific mode — drop them
        // when switching back.
        if (state.type === "Notes" || state.type === "FollowUps") {
          const was = state.type;
          state.type = "";
          state.referenceKey = "";
          if (
            (was === "Notes" &&
              state.errorMessage === NOTE_REQUIREMENT_ERROR_MESSAGE) ||
            (was === "FollowUps" &&
              state.errorMessage === FOLLOWUP_REQUIREMENT_ERROR_MESSAGE)
          ) {
            state.errorMessage = DEFAULT_ERROR_MESSAGE;
          }
        }
      } else if (!state.selectedStatusIds.length && state.selectedStatusId) {
        // Entering specific mode carries the current single pick over.
        state.selectedStatusIds = [state.selectedStatusId];
      }
    },
    setRequirementAlsoRequireNote(state, action: PayloadAction<boolean>) {
      state.alsoRequireNote = action.payload;
    },
    setRequirementReferenceKey(state, action: PayloadAction<string>) {
      state.referenceKey = action.payload;
    },
    setRequirementReferenceId(state, action: PayloadAction<number | null>) {
      state.referenceId = action.payload;
    },
    setRequirementIsBlocking(state, action: PayloadAction<boolean>) {
      state.isBlocking = action.payload;
    },
    setRequirementIsInfo(state, action: PayloadAction<boolean>) {
      state.isInfo = action.payload;
      // Info never enforces — keep the pair consistent for old readers.
      if (action.payload) state.isBlocking = false;
    },
    setRequirementErrorMessage(state, action: PayloadAction<string>) {
      state.errorMessage = action.payload;
    },
    setRequirementExcludedRoleIds(state, action: PayloadAction<number[]>) {
      state.excludedRoleIds = [...new Set(action.payload)].filter(
        (n) => Number.isFinite(n) && n > 0,
      );
    },
    setDeleteRequirementId(state, action: PayloadAction<number | null>) {
      state.deleteRequirementId = action.payload;
    },
    /**
     * Bind the form to an existing requirement. Every field is set at once
     * (rather than replayed through the individual setters) so the type-change
     * side effects — which clear `referenceKey`/`referenceId` and swap the
     * default error message — can't wipe the row being loaded.
     */
    startRequirementEdit(state, action: PayloadAction<Requirement>) {
      const req = action.payload;
      state.editingRequirementId = req.id;
      state.selectedStatusId = String(req.statusId);
      state.selectedStatusIds = [String(req.statusId)];
      state.selectedSubStatusIds = req.subStatusId
        ? [String(req.subStatusId)]
        : [];
      state.type = req.type;
      state.referenceKey = req.referenceKey ?? "";
      state.referenceId = req.referenceId ?? null;
      state.isBlocking = req.isBlocking ?? true;
      state.isInfo = req.isInfo ?? false;
      state.errorMessage = req.errorMessage ?? DEFAULT_ERROR_MESSAGE;
      state.applyMode = req.applyMode ?? "hierarchy";
      state.excludedRoleIds = req.excludedRoleIds ?? [];
      // The "also make note mandatory" provision creates a SECOND row; it has
      // no meaning while editing one.
      state.alsoRequireNote = false;
    },
    /** Leave edit mode and hand the form back to adding, dialog still open. */
    cancelRequirementEdit(state) {
      return {
        ...initialState,
        isAddRequirement: state.isAddRequirement,
        scope: state.scope,
        trackId: state.trackId,
      };
    },
    /** After a save: a clean form in the SAME scope, dialog still open. */
    clearRequirementFormKeepOpen(state) {
      return {
        ...initialState,
        isAddRequirement: true,
        scope: state.scope,
        trackId: state.trackId,
      };
    },
    resetRequirementForm() {
      return initialState;
    },
  },
});

/* ------------------------------------------------------------------ */
/* ACTIONS                                                             */
/* ------------------------------------------------------------------ */

export const {
  setIsAddRequirement,
  openTrackRequirementForm,
  clearRequirementFormKeepOpen,
  setRequirementSelectedStatusId,
  setRequirementSelectedStatusIds,
  setRequirementSelectedSubStatusIds,
  setRequirementType,
  setRequirementApplyMode,
  setRequirementAlsoRequireNote,
  setRequirementReferenceKey,
  setRequirementReferenceId,
  setRequirementIsBlocking,
  setRequirementIsInfo,
  setRequirementErrorMessage,
  setRequirementExcludedRoleIds,
  setDeleteRequirementId,
  startRequirementEdit,
  cancelRequirementEdit,
  resetRequirementForm,
} = requirementFunctionalSlice.actions;

export default requirementFunctionalSlice.reducer;
